import { Injectable, Injector, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouteReuseStrategy,
} from '@angular/router';

interface StoredRoute {
  handle: DetachedRouteHandle;
  scrollY: number;
}

const VEHICLE_DETAIL_PATTERN = /^\/(captar|avaliar)\/veiculos\/[^/]+\/[^/]+/;

/**
 * Estratégia de reuso que mantém vivo o componente de rotas marcadas com
 * `data: { reuseComponent: true }`, preservando estado e posição de scroll.
 *
 * O reuso só acontece no fluxo lista -> detalhe do veículo -> volta para a lista.
 * Qualquer outra navegação (menu, troca de concessionária, reload) recria o
 * componente normalmente, garantindo dados atualizados.
 */
@Injectable({ providedIn: 'root' })
export class AppRouteReuseStrategy implements RouteReuseStrategy {
  // Resolvido de forma adiada para evitar dependência circular com o Router.
  private readonly injector = inject(Injector);
  private router: Router | null = null;
  private readonly stored = new Map<string, StoredRoute>();
  private pendingScrollY: number | null = null;

  private navigationFromUrl = '';
  private navigationToUrl = '';

  // Flag de uso único: força a recriação do componente-folha na próxima navegação.
  private forceReloadLeafOnce = false;

  constructor() {
    Promise.resolve().then(() => this.setupNavigationTracking());
  }

  private setupNavigationTracking(): void {
    if (this.router) {
      return;
    }
    this.router = this.injector.get(Router);
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.navigationFromUrl = this.normalize(this.router!.url);
        this.navigationToUrl = this.normalize(event.url);
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.forceReloadLeafOnce = false;
      }
    });
  }

  /**
   * Força a recriação do componente-folha na próxima navegação (uso único).
   * Substitui o padrão depreciado de mutar `router.routeReuseStrategy.shouldReuseRoute`.
   * Usado por telas de resumo que filtram via query params e precisam reexecutar
   * a busca da listagem.
   */
  forceReloadOnce(): void {
    this.forceReloadLeafOnce = true;
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return this.shouldReuse(route) && VEHICLE_DETAIL_PATTERN.test(this.navigationToUrl);
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const key = this.buildKey(route);
    if (!key) {
      return;
    }
    if (handle) {
      this.stored.set(key, { handle, scrollY: window.scrollY });
    } else {
      this.stored.delete(key);
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = this.buildKey(route);
    if (!key) {
      return false;
    }

    if (!VEHICLE_DETAIL_PATTERN.test(this.navigationFromUrl)) {
      this.stored.delete(key);
      return false;
    }
    return this.stored.has(key);
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.buildKey(route);
    if (!key) {
      return null;
    }
    const entry = this.stored.get(key);
    if (!entry) {
      return null;
    }
    this.pendingScrollY = entry.scrollY;
    queueMicrotask(() => this.applyPendingScroll());
    return entry.handle;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    const sameConfig = future.routeConfig === curr.routeConfig;
    // Recria apenas o componente-folha (mantém o layout/header vivo).
    if (this.forceReloadLeafOnce && sameConfig && future.firstChild === null) {
      return false;
    }
    return sameConfig;
  }

  /** Invalida o cache (todas as rotas ou apenas uma). */
  invalidate(routeKey?: string): void {
    if (!routeKey) {
      this.stored.clear();
      return;
    }
    this.stored.delete(routeKey);
  }

  private applyPendingScroll(): void {
    if (this.pendingScrollY == null) {
      return;
    }
    const target = this.pendingScrollY;
    this.pendingScrollY = null;
    const tryScroll = () => window.scrollTo(0, target);
    requestAnimationFrame(() => requestAnimationFrame(tryScroll));
  }

  private shouldReuse(route: ActivatedRouteSnapshot): boolean {
    return route.data?.['reuseComponent'] === true;
  }

  private buildKey(route: ActivatedRouteSnapshot): string | null {
    if (!this.shouldReuse(route)) {
      return null;
    }
    return route.pathFromRoot
      .map((segment) => segment.url.map((u) => u.path).join('/'))
      .filter(Boolean)
      .join('/');
  }

  private normalize(url: string): string {
    return url.split('?')[0];
  }
}
